import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook to track online/offline status with reconnection detection
 * and active ping verification.
 */
export const useOnlineStatus = () => {
  const [isOnline, setIsOnline] = useState(() => {
    return typeof navigator !== 'undefined' ? navigator.onLine : true;
  });
  const [wasOffline, setWasOffline] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const reconnectedTimerRef = useRef(null);

  // Active ping verification to ensure real network connectivity
  const checkConnectivity = useCallback(async () => {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      setIsOnline(false);
      setWasOffline(true);
      return false;
    }

    setIsChecking(true);
    try {
      const response = await fetch(`/favicon.ico?_ping=${Date.now()}`, {
        method: 'HEAD',
        cache: 'no-store'
      });
      const online = response.ok || response.status === 304;
      setIsOnline(online);
      if (!online) {
        setWasOffline(true);
      }
      return online;
    } catch (error) {
      setIsOnline(false);
      setWasOffline(true);
      return false;
    } finally {
      setIsChecking(false);
    }
  }, []);

  useEffect(() => {
    const handleOnline = async () => {
      const trulyOnline = await checkConnectivity();
      if (trulyOnline) {
        setIsOnline(true);
        // Keep wasOffline true for 3 seconds to show reconnection badge
        if (reconnectedTimerRef.current) {
          clearTimeout(reconnectedTimerRef.current);
        }
        reconnectedTimerRef.current = setTimeout(() => {
          setWasOffline(false);
        }, 3500);
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      setWasOffline(true);
      if (reconnectedTimerRef.current) {
        clearTimeout(reconnectedTimerRef.current);
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (reconnectedTimerRef.current) {
        clearTimeout(reconnectedTimerRef.current);
      }
    };
  }, [checkConnectivity]);

  return {
    isOnline,
    isOffline: !isOnline,
    wasOffline,
    isChecking,
    checkConnectivity
  };
};

export default useOnlineStatus;
